import { SecurityInventoryCategory, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import { renderToString } from 'react-dom/server';
import L from 'leaflet';
import { Identifier } from 'react-admin';
import {
  Marker,
} from 'react-leaflet';
import {
  MutableRefObject, useEffect, useRef, useState, useMemo,
} from 'react';
import { InventoryCategoryIconName, inventoryCategoryIconMap } from '../../../components/icons/inventoryCategoryIcons';
import { hasItemPosition, securityItemsResource } from '../util';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';

const SELECTED_FILL = '#e53935';

const resolveCategoryIconName = (
  item: SecurityInventoryItem,
  categories: SecurityInventoryCategory[],
) => {

  const category = categories.find((c) => c._id === item.category._id);
  if (!category) return null;

  return category.icon as InventoryCategoryIconName;

};

const getInventoryDivIcon = (
  markerId: string,
  iconName: InventoryCategoryIconName,
  label: string,
  selected: boolean,
) => {

  const Icon = inventoryCategoryIconMap[iconName];
  const fillBackground = selected ? SELECTED_FILL : '#fff';

  const html = renderToString(
    <div
      id={`inventory-marker-${markerId}`}
      style={{
        textAlign: 'center',
        width: 110,
        margin: '0 auto',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Main square */}
        <div
          style={{
            width: 28,
            height: 28,
            boxSizing: 'border-box',
            background: fillBackground,
            border: `2px solid ${SELECTED_FILL}`,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: 19,
              height: 19,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon
              style={{
                fill: selected ? '#fff' : '#000',
              }}
            />
          </div>
        </div>
        {/* Arrow rectangle */}
        <div
          style={{
            width: 10,
            height: 6,
            background: fillBackground,
            borderLeft: `2px solid ${SELECTED_FILL}`,
            borderRight: `2px solid ${SELECTED_FILL}`,
            borderBottom: `2px solid ${SELECTED_FILL}`,
            boxSizing: 'border-box',
          }}
        />

        {/* Arrow tip */}
        <div
          style={{
            position: 'relative',
            width: 16,
            height: 10,
            marginTop: -1,
          }}
        >
          {/* Outer red triangle */}
          <div
            style={{
              position: 'absolute',
              top: -1,
              left: 1,
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: `10px solid ${SELECTED_FILL}`,
            }}
          />

          {/* Inner triangle */}
          <div
            style={{
              position: 'absolute',
              top: -1,
              left: 2,
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `8px solid ${fillBackground}`,
            }}
          />
        </div>
        {/* Label */}
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: '#000',
            maxWidth: 100,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </div>
      </div>
    </div>,
  );

  const pinTipY = 28 + 6 + 8;
  const totalH = pinTipY + 18;

  return new L.DivIcon({
    html,
    className: 'inventory-item-marker',
    iconSize: [110, totalH],
    iconAnchor: [55, pinTipY],
    popupAnchor: [0, -pinTipY],
  });

};

export const ItemMapMarker = (props: {
  item: SecurityInventoryItem;
  categories: SecurityInventoryCategory[];
  focusedItemId: Identifier | null;
  setFocusedItemId: React.Dispatch<React.SetStateAction<Identifier | null>>;
  moveLocked: boolean;
}) => {

  const markerRef:MutableRefObject<L.Marker | null> = useRef(null);
  const [position, setPosition] = useState<[number, number] | null>(null);

  const iconName = useMemo(
    () => resolveCategoryIconName(props.item, props.categories),
    [props.item, props.categories],
  );

  const selected = props.focusedItemId === props.item._id;

  const icon = useMemo(() => {

    if (!iconName) return null;

    return getInventoryDivIcon(
      props.item._id,
      iconName,
      props.item.name,
      selected,
    );

  }, [props.item._id, props.item.name, iconName, selected]);
  const dragAndUpdatePosition = (item: SecurityInventoryItem, coordinates: [number, number]) => {

    xgacDataProvider.update(securityItemsResource, {
      id: item._id,
      data: {
        location: {
          ...item.location,
          coordinates: [coordinates[1], coordinates[0]],
        },
      },
      previousData: item,
    });

  };

  useEffect(() => {

    if (!markerRef.current) return;
    if (!props.moveLocked) markerRef.current.dragging?.enable();
    else markerRef.current.dragging?.disable();

  }, [props.moveLocked, props.item]);

  useEffect(() => {

    if (!hasItemPosition(props.item)) {

      setPosition(null);
      return;

    }
    setPosition([props.item.location.coordinates[1] || 0, props.item.location.coordinates[0] || 0]);

  }, [props.item]);

  if (!hasItemPosition(props.item) || !position) return null;
  if (!icon) return null;

  return (
    <Marker
      key={props.item._id}
      position={position}
      icon={icon}
      draggable={!props.moveLocked}
      ref={markerRef}
      eventHandlers={{
        click: () => {

          props.setFocusedItemId(props.item._id);

        },
        dragend: (e) => {

          if (props.moveLocked) return;

          const { lat, lng } = e.target.getLatLng();
          setPosition([lat, lng]);
          dragAndUpdatePosition(props.item, [lat, lng]);

        },
      }}
    >
    </Marker>
  );

};
